import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { cn } from '@shared/lib/cn';
import { fetchAccessGateEvents, markAccessGateEventRead, type AccessGateEvent } from '../api';
import type { VisitStep } from '../PersonVisits';
import { dateOf, groupPin, lastMoment, startOfToday, timeOf, untilLabel, untilWhen, whenOf } from '../visitorAccessFormat';
import { BADGE, BADGE_BRASS, CARD, ROW_DIVIDER, WIDGET } from '../visitorAccessStyles';
import { ListSheet } from './ListSheet';
import { Row } from './Row';
import { BOOKING_LABEL, useActivePins, type GuardPin, type YourPin } from './useActivePins';
import { VisitSheet, type VisitTarget } from './VisitSheet';

export const GATE_EVENTS_QUERY_KEY = ['access-gate-events'] as const;

/** Which way they went. The gate's own message names the gate, but only as free text. */
const DIRECTION: Record<string, string> = { entry: 'Gate (in)', exit: 'Gate (out)', sos: 'SOS alert' };

/** A gate event as a step in a visit's story. */
const STEP_LABEL: Record<string, string> = { entry: 'Came in', exit: 'Left', sos: 'SOS alert' };

/** Gate events that are someone going in or out. */
const PASS_TYPES = new Set(['entry', 'exit']);

/** How many of today's rows the card shows; the rest are on Active PINs and in History. */
const SHOWN = 4;

export type State = 'on_site' | 'expected' | 'left';

const STATE_BADGE: Record<State, { label: string; className: string }> = {
  on_site: { label: 'On site', className: BADGE_BRASS },
  expected: { label: 'Expected', className: BADGE },
  left: { label: 'Left', className: 'shrink-0 rounded-[8px] bg-hairline px-2 py-1 text-xs font-semibold text-ink-secondary' },
};

interface TodayRow {
  key: string;
  name: string;
  line: string;
  state: State;
  /** Sorts within a state: the start of a visit, or the last time through the gate. */
  at: number;
  /** What tapping the row opens: the visit's drawer, or a saved group's page. */
  open: VisitTarget | { groupId: string };
}

/**
 * Today at the gate in one card: who is on site, who is expected and who has left, from the member's
 * live PINs, the guard's PINs and today's gate events. "See all history" opens the full History in a
 * drawer that is part of the URL (`?view=arrivals`), so a gate notification can open it.
 */
export function TodayCard(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const historyOpen = searchParams.get('view') === 'arrivals';
  const setHistoryOpen = (open: boolean) => setSearchParams(open ? { view: 'arrivals' } : {}, { replace: true });

  const pins = useActivePins();
  const eventsQuery = useQuery({ queryKey: GATE_EVENTS_QUERY_KEY, queryFn: () => fetchAccessGateEvents() });
  const readMutation = useMutation({
    mutationFn: markAccessGateEventRead,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: GATE_EVENTS_QUERY_KEY }),
  });
  // The visit stays set while its drawer slides away, so the drawer never empties mid-close.
  const [visit, setVisit] = useState<VisitTarget | null>(null);
  const [visitOpen, setVisitOpen] = useState(false);
  const openVisit = (target: VisitTarget) => {
    setVisit(target);
    setVisitOpen(true);
  };

  const events = eventsQuery.data ?? [];
  const rows = todayRows(pins.yourPins, pins.guardPins, events);
  const expected = rows.filter((row) => row.state === 'expected').length;
  const onSite = rows.filter((row) => row.state === 'on_site').length;
  const summary = [expected ? `${expected} expected` : '', onSite ? `${onSite} on site` : ''].filter(Boolean).join(' · ');

  if (pins.isLoading || eventsQuery.isLoading) return <Skeleton className="h-60 rounded-[22px]" />;
  if (eventsQuery.isError) return <ErrorState onRetry={() => eventsQuery.refetch()} />;

  return (
    <section className={cn(WIDGET, 'flex flex-col pl-[18px] pr-4 pt-4')}>
      {/* 1. The day at a glance. */}
      <div className="flex flex-col gap-0.5 pb-1">
        <h2 className="m-0 text-base font-semibold text-ink">Today</h2>
        <p className="m-0 text-[13px] text-ink-secondary">{summary || (rows.length ? 'Nobody on site' : 'Quiet so far')}</p>
      </div>

      {/* 2. On site first, then who is still coming, then who has left. */}
      {rows.length ? (
        <div>
          {rows.slice(0, SHOWN).map((row) => (
            <Row
              key={row.key}
              className={cn(ROW_DIVIDER, 'px-0')}
              leading={
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-paper text-[15px] font-semibold text-primary-800">
                  {row.name.charAt(0).toUpperCase()}
                </span>
              }
              title={row.name}
              line={row.line}
              trailing={<span className={STATE_BADGE[row.state].className}>{STATE_BADGE[row.state].label}</span>}
              onClick={() => ('groupId' in row.open ? navigate(`/app/access-invites/groups/${row.open.groupId}`) : openVisit(row.open))}
            />
          ))}
        </div>
      ) : (
        <p className="m-0 pb-4 pt-2.5 text-sm text-ink-secondary">Nobody expected and nobody has come through yet.</p>
      )}

      <button
        type="button"
        onClick={() => setHistoryOpen(true)}
        className="min-h-11 w-full border-t border-hairline text-sm font-semibold text-primary-500"
      >
        See all history
      </button>

      {/* 3. Every gate event. Tapping one opens its visit, and marks it read, as the gate notification it came with. */}
      <ListSheet open={historyOpen} onOpenChange={setHistoryOpen} title="History">
        {events.length ? (
          <div className={cn(CARD, 'overflow-hidden')}>
            {events.map((event) => (
              <Row
                key={event.access_gate_event_id}
                className={ROW_DIVIDER}
                {...describeEvent(event)}
                chevron
                onClick={() => {
                  if (!event.is_read) readMutation.mutate(event.access_gate_event_id);
                  openVisit(eventTarget([event], event.access_invite_id));
                }}
              />
            ))}
          </div>
        ) : (
          <p className="m-0 text-sm text-ink-secondary">No gate activity yet.</p>
        )}
      </ListSheet>

      {/* 4. One visit, opened from Today or from History. */}
      <VisitSheet target={visit} open={visitOpen} onOpenChange={setVisitOpen} />
    </section>
  );
}

/**
 * Today's rows. A PIN whose visit touches today is expected until the gate sees them: then on site,
 * or left once they go out. Gate events that match none of those PINs get a row of their own.
 */
export function todayRows(yourPins: YourPin[], guardPins: GuardPin[], events: AccessGateEvent[]): TodayRow[] {
  const dayStart = startOfToday().getTime();
  const dayEnd = dayStart + 86_400_000;

  // 1. Today's comings and goings, oldest first, by the PIN they used (or the name the gate gave).
  const passes = events
    .filter((event) => PASS_TYPES.has(event.message_type.toLowerCase()) && new Date(event.created_at).getTime() >= dayStart)
    .sort((a, b) => a.created_at.localeCompare(b.created_at));
  // An event the gate didn't tie to a PIN still counts for the visitor of the same name.
  const passesFor = (inviteIds: string[], name: string) =>
    passes.filter((event) => (event.access_invite_id ? inviteIds.includes(event.access_invite_id) : event.visitor_name === name));
  const used = new Set<AccessGateEvent>();

  const rows: TodayRow[] = [];
  const touchesToday = (from?: string | null, until?: string | null) =>
    (!from || new Date(from).getTime() < dayEnd) && (!until || new Date(until).getTime() > dayStart);

  // 2. The member's own PINs for today.
  for (const pin of yourPins) {
    const invites = pin.kind === 'group' ? pin.invites : [pin.invite];
    const { valid_from: from, valid_until: until } = invites[0]!;
    if (!touchesToday(from, until)) continue;

    const name =
      pin.kind === 'group'
        ? `${pin.name} · ${pin.invites.length === 1 ? '1 person' : `${pin.invites.length} people`}`
        : pin.kind === 'booking'
          ? `${pin.invite.visitor_name ?? BOOKING_LABEL[pin.booking]} · ${BOOKING_LABEL[pin.booking]}`
          : pin.name;
    const seen = passesFor(invites.map((invite) => invite.access_invite_id), name);
    seen.forEach((event) => used.add(event));
    const open: TodayRow['open'] =
      pin.kind === 'group'
        ? { groupId: pin.groupId }
        : { day: from ?? pin.invite.created_at ?? new Date().toISOString(), inviteId: pin.invite.access_invite_id, steps: passSteps(seen), details: [] };
    rows.push(visitRow(`pin:${pin.kind === 'group' ? pin.key : pin.invite.access_invite_id}`, name, from, until, seen, pin.kind === 'person' && pin.arrived, open));
  }

  // 3. PINs the guard made at the gate for today.
  for (const pin of guardPins) {
    if (!touchesToday(pin.valid_from ?? pin.created_at, pin.valid_until)) continue;
    const name = pin.visitor_name ?? 'Visitor';
    const seen = passesFor([], name);
    seen.forEach((event) => used.add(event));
    const open: VisitTarget = {
      day: pin.valid_from ?? pin.created_at ?? new Date().toISOString(),
      steps: [{ label: 'PIN made by the guard', meta: pin.created_at ? stepTime(pin.created_at) : 'At the gate', done: true }, ...passSteps(seen)],
      details: [
        ['PIN', pin.pin ? groupPin(pin.pin) : null],
        ['PIN works', untilLabel(pin.valid_until)],
      ],
    };
    rows.push(visitRow(`guard:${pin.case_id}`, name, pin.valid_from, pin.valid_until, seen, false, open));
  }

  // 4. Anyone else through the gate today, one row per visitor.
  const others = new Map<string, AccessGateEvent[]>();
  for (const event of passes) {
    if (used.has(event)) continue;
    const key = event.access_invite_id ?? event.visitor_name ?? event.access_gate_event_id;
    others.set(key, [...(others.get(key) ?? []), event]);
  }
  for (const [key, seen] of others) {
    const last = seen[seen.length - 1]!;
    rows.push({ key: `gate:${key}`, name: last.visitor_name ?? last.message, ...passLine(seen), open: eventTarget(seen, last.access_invite_id) });
  }

  // 5. On site, then expected by start time, then left with the latest first.
  const order: Record<State, number> = { on_site: 0, expected: 1, left: 2 };
  return rows.sort((a, b) => order[a.state] - order[b.state] || (a.state === 'expected' ? a.at - b.at : b.at - a.at));
}

/** A PIN's row: where they stand at the gate, and its times. */
function visitRow(
  key: string,
  name: string,
  from: string | null | undefined,
  until: string | null | undefined,
  seen: AccessGateEvent[],
  arrived: boolean,
  open: TodayRow['open'],
): TodayRow {
  if (seen.length) {
    const pass = passLine(seen);
    // On site, the PIN's end matters more than the way in.
    return pass.state === 'on_site' ? { key, name, ...pass, line: `${pass.line} · PIN ${untilWhen(until ?? null)}`, open } : { key, name, ...pass, open };
  }

  const start = from ? new Date(from) : null;
  return {
    key,
    name,
    line: pinWindow(start, until ?? null),
    state: arrived ? 'on_site' : 'expected',
    at: start?.getTime() ?? 0,
    open,
  };
}

/** What the gate recorded, for a visit with no PIN of the member's behind it (or one that won't load). */
function eventTarget(seen: AccessGateEvent[], inviteId?: string | null): VisitTarget {
  const last = seen[seen.length - 1]!;
  return {
    day: last.created_at,
    inviteId: inviteId ?? undefined,
    steps: passSteps(seen),
    // Without a visitor name the gate's message is already the row's title.
    details: last.visitor_name ? [['Message', last.message]] : [],
  };
}

/** Each time through the gate as a step: "Came in" over "16 Oct, 15:00". */
function passSteps(seen: AccessGateEvent[]): VisitStep[] {
  return seen.map((event) => ({
    label: STEP_LABEL[event.message_type.toLowerCase()] ?? 'Gate message',
    meta: stepTime(event.created_at),
    done: true,
  }));
}

/** "16 Oct, 15:00", as a person's visit drawer writes it. */
function stepTime(iso: string): string {
  return `${dateOf(iso)}, ${timeOf(iso)}`;
}

/** "PIN 17:00 to 22:00" when it starts and ends today; "PIN until 18:00" once it has started. */
function pinWindow(start: Date | null, until: string | null): string {
  if (!start || start.getTime() <= Date.now()) return `PIN ${untilWhen(until)}`;
  if (until && lastMoment(until).toDateString() === start.toDateString()) return `PIN ${timeOf(start)} to ${timeOf(lastMoment(until))}`;
  return `PIN from ${timeOf(start)} ${untilWhen(until)}`;
}

/** "Came in 15:00" while on site; "In 12:31 · out 12:40" once they've gone. */
function passLine(seen: AccessGateEvent[]): Pick<TodayRow, 'line' | 'state' | 'at'> {
  const last = seen[seen.length - 1]!;
  const at = new Date(last.created_at).getTime();
  if (last.message_type.toLowerCase() !== 'exit') return { line: `Came in ${timeOf(last.created_at)}`, state: 'on_site', at };

  const entry = [...seen].reverse().find((event) => event.message_type.toLowerCase() === 'entry');
  return { line: entry ? `In ${timeOf(entry.created_at)} · out ${timeOf(last.created_at)}` : `Out ${timeOf(last.created_at)}`, state: 'left', at };
}

/**
 * Who came through, over which way and when: "Jane Doe" over "Gate (in) · today 08:12". The
 * visitor's name comes from the PIN the event matched; without one, the gate's message says who.
 */
function describeEvent(event: AccessGateEvent): { title: string; line: string } {
  const direction = DIRECTION[event.message_type.toLowerCase()];
  const what = direction ?? (event.visitor_name ? event.message : null);
  return {
    title: event.visitor_name ?? event.message,
    line: [what, whenOf(event.created_at)].filter(Boolean).join(' · '),
  };
}
